import { useQuery } from "@tanstack/react-query"
import { getSuppliers, findSupplierByCode, getSupplierMetrics } from "src/services/supplierService"
import { apiRequest } from "src/services/apiClient"
import { queryKeys } from "./queryKeys"
import { CATALOGUE_STALE_TIME } from "src/services/queryClient"
import type { Supplier } from "src/types/supplier"

/**
 * Suppliers list for a facility, via TanStack Query.
 *
 * Wraps the existing supplierService.getSuppliers (which goes through
 * apiClient — so Cognito auth, 401 refresh, timeout, and error normalisation
 * are all preserved). The supplier catalogue changes rarely, so it uses the
 * 30-min catalogue staleTime (matches the old apiCache TTL rule).
 *
 * Returns the unwrapped Supplier[] (the API wraps it in `{ data }`).
 */
export function useSuppliers(facility: string) {
  return useQuery({
    queryKey: queryKeys.suppliers.list(facility),
    queryFn: async (): Promise<Supplier[]> => {
      const response = await getSuppliers(facility)
      return response.data ?? []
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility),
  })
}

/**
 * A single supplier by code, with multi-facility fallback (see
 * findSupplierByCode). Returns null when not found anywhere.
 */
export function useSupplierDetail(facility: string, code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.suppliers.detail(facility, code ?? ""),
    queryFn: () => findSupplierByCode(facility, code!),
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && code),
  })
}

export interface SupplierPoSummary {
  id: string
  poNumber: string
  status: string
  orderDate: string
  totalAmount?: number
}

/** The 5 most recent POs for a supplier. */
export function useSupplierRecentPos(facility: string, code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.suppliers.recentPos(facility, code ?? ""),
    queryFn: async (): Promise<SupplierPoSummary[]> => {
      const response = await apiRequest<{ data: SupplierPoSummary[] }>(
        `/po?facility=${encodeURIComponent(facility)}&supplier=${encodeURIComponent(code!)}`,
      )
      return (response.data ?? []).slice(0, 5)
    },
    enabled: Boolean(facility && code),
  })
}

export interface SupplierItemSummary {
  code: string
  description: string
  unitOfMeasure?: string
}

/** Up to 10 items supplied by a supplier. */
export function useSupplierItems(facility: string, code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.suppliers.items(facility, code ?? ""),
    queryFn: async (): Promise<SupplierItemSummary[]> => {
      const response = await apiRequest<{ data: SupplierItemSummary[] }>(
        `/items?facility=${encodeURIComponent(facility)}&supplier=${encodeURIComponent(code!)}`,
      )
      return (response.data ?? []).slice(0, 10)
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && code),
  })
}

/**
 * Supplier performance metrics. Computed from PO history, so NOT a catalogue —
 * uses the default staleTime. `enabled` is driven by a submitted supplier code
 * so the query only runs once the user picks a supplier and clicks (the page
 * sets the code on submit).
 */
export function useSupplierMetrics(facility: string, submittedCode: string) {
  return useQuery({
    queryKey: queryKeys.suppliers.metrics(facility, submittedCode),
    queryFn: () => getSupplierMetrics(facility, submittedCode),
    enabled: Boolean(facility && submittedCode),
  })
}
